import { useCallback, useMemo, useState } from 'react';

import { type Clip } from '@air/api/types';
import { getClipAspectRatio } from '@air/utils-clips';

export type UseImageOptionsParams = {
  /** The active clip — used only for aspect ratio computation */
  clip: Clip | undefined;
  /** Initial values to restore previous selections when reopening the panel */
  initialAssetOptions?: {
    width?: number;
    height?: number;
  };
};

export const useImageOptions = ({ clip, initialAssetOptions }: UseImageOptionsParams) => {
  const [customWidth, setCustomWidth] = useState(initialAssetOptions?.width ?? clip?.darWidth ?? clip?.width ?? 0);
  const [customHeight, setCustomHeight] = useState(initialAssetOptions?.height ?? clip?.darHeight ?? clip?.height ?? 0);
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(true);

  const aspectRatio = useMemo(() => (clip ? getClipAspectRatio(clip) : 0), [clip]);

  const resetDimensionsForClip = useCallback((newClip: Clip) => {
    setCustomWidth(newClip.darWidth ?? newClip.width ?? 0);
    setCustomHeight(newClip.darHeight ?? newClip.height ?? 0);
  }, []);

  const handleWidthChange = useCallback(
    (newWidth: number) => {
      setCustomWidth(newWidth);
      if (isAspectRatioLocked && aspectRatio > 0) {
        setCustomHeight(Math.round(newWidth / aspectRatio));
      }
    },
    [isAspectRatioLocked, aspectRatio],
  );

  const handleHeightChange = useCallback(
    (newHeight: number) => {
      setCustomHeight(newHeight);
      if (isAspectRatioLocked && aspectRatio > 0) {
        setCustomWidth(Math.round(newHeight * aspectRatio));
      }
    },
    [isAspectRatioLocked, aspectRatio],
  );

  const handleToggleAspectRatioLock = useCallback(() => {
    const newLockState = !isAspectRatioLocked;
    setIsAspectRatioLocked(newLockState);

    if (newLockState && aspectRatio > 0 && customWidth) {
      setCustomHeight(Math.round(customWidth / aspectRatio));
    }
  }, [isAspectRatioLocked, aspectRatio, customWidth]);

  return {
    customHeight,
    customWidth,
    handleHeightChange,
    handleToggleAspectRatioLock,
    handleWidthChange,
    isAspectRatioLocked,
    resetDimensionsForClip,
  };
};
